import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import StripeWrapper from '../../components/StripeWrapper';
import SpaceMonkeyLogo from '../../components/brand/SpaceMonkeyLogo';
import Logo from '../../components/brand/Logo';
import Icon from '../../components/brand/Icon';
import parkingApi from '../../services/parkingApi';
import './SpaceMonkeyPayPage.css';

// This page serves the Space Monkey lot. Other vendors get their own slug.
const LOT_SLUG = 'spacemonkey';

const PARKING_FEE = 5.00;
const SERVICE_FEE = 1.00;
const TOTAL_AMOUNT = PARKING_FEE + SERVICE_FEE;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const PaymentForm = ({ licensePlate, state, email, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setCardError('');

    try {
      // 1. Server creates the PaymentIntent + a pending permit (amount is
      //    authoritative from the lot record).
      const { clientSecret, payment_intent_id, permit_number, expires_at } =
        await parkingApi.createPermitIntent({ slug: LOT_SLUG, licensePlate, state, email });

      // 2. Confirm the card payment with Stripe.
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email: email || undefined },
        },
        receipt_email: email || undefined,
      });

      if (error) {
        setCardError(error.message);
        onError(error.message);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // 3. Activate the permit immediately so enforcement sees it at once.
        try {
          await parkingApi.confirmPermit(payment_intent_id);
        } catch (confirmErr) {
          // Payment succeeded; the Stripe webhook will still activate the permit.
          console.warn('confirm-permit failed, webhook will finalize:', confirmErr.message);
        }
        onSuccess({ permitNumber: permit_number, expiresAt: expires_at });
      } else {
        setCardError(`Unexpected payment status: ${paymentIntent.status}`);
      }
    } catch (err) {
      setCardError(err.message || 'Payment failed. Please try again.');
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePay} className="sm-pay-stripe-form">
      <div className="sm-pay-card-wrap">
        <label className="sm-pay-label">Card Details</label>
        <div className="sm-pay-card-element">
          <CardElement options={{
            style: {
              base: { fontSize: '16px', color: '#111827', '::placeholder': { color: '#9ca3af' } },
              invalid: { color: '#dc2626' },
            },
            hidePostalCode: true,
          }} />
        </div>
      </div>
      {cardError && <div className="sm-pay-error">{cardError}</div>}
      <button type="submit" className="btn btn-primary btn-lg sm-pay-btn" disabled={loading || !stripe}>
        {loading ? <><span className="spinner"></span> Processing...</> : `Pay $${TOTAL_AMOUNT.toFixed(2)} and Get Permit`}
      </button>
      <div className="sm-pay-secure">
        <Icon name="lock" size={15} /> Secure payment powered by Stripe. Your card is never stored.
      </div>
    </form>
  );
};

const SpaceMonkeyPayPage = () => {
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({ licensePlate: '', state: 'TX', email: '' });
  const [errors, setErrors] = useState({});
  const [permitId, setPermitId] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData.licensePlate.trim()) errs.licensePlate = 'License plate is required';
    if (formData.licensePlate.trim().length > 10) errs.licensePlate = 'Check plate number';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Enter a valid email';
    return errs;
  };

  const handleContinue = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep('pay');
  };

  const handleSuccess = ({ permitNumber }) => {
    if (permitNumber) setPermitId(permitNumber);
    setStep('success');
  };
  const handleError = () => {};

  if (step === 'success') {
    return (
      <div className="sm-pay-page">
        <div className="sm-pay-success">
          <div className="sm-success-icon"><Icon name="check" size={40} /></div>
          <h1>Permit Active</h1>
          <div className="sm-permit-id">{permitId}</div>
          <div className="sm-permit-details">
            <div><span>Plate</span><strong>{formData.licensePlate.toUpperCase()} · {formData.state}</strong></div>
            <div><span>Location</span><strong>Space Monkey, Edinburg, TX</strong></div>
            <div><span>Valid</span><strong>Today only</strong></div>
            <div><span>Amount Paid</span><strong>${TOTAL_AMOUNT.toFixed(2)}</strong></div>
          </div>
          <p className="sm-success-note">
            {formData.email ? `Receipt sent to ${formData.email}.` : 'Screenshot this screen or note your permit ID as proof of payment.'}
          </p>
          <div className="sm-success-actions">
            <Link to="/spacemonkey" className="btn btn-outline">Back to Space Monkey</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sm-pay-page">
      <div className="sm-pay-box">
        {/* Brand */}
        <div className="sm-pay-brand">
          <Link to="/spacemonkey" className="sm-pay-back-logo">
            <SpaceMonkeyLogo size="sm" tagline={false} />
          </Link>
          <span className="sm-pay-powered">
            <span>Powered by</span> <Logo size="sm" />
          </span>
        </div>

        <div className="sm-pay-header">
          <div className="sm-pay-price-badge">${TOTAL_AMOUNT.toFixed(2)}</div>
          <h1>Parking Permit</h1>
          <p>Space Monkey Parking, Edinburg, TX</p>
        </div>

        <div className="sm-pay-fee-breakdown">
          <div className="sm-pay-fee-row">
            <span>Parking permit</span>
            <span>${PARKING_FEE.toFixed(2)}</span>
          </div>
          <div className="sm-pay-fee-row">
            <span>Convenience fee</span>
            <span>${SERVICE_FEE.toFixed(2)}</span>
          </div>
          <div className="sm-pay-fee-row sm-pay-fee-total">
            <span>Total charged</span>
            <span>${TOTAL_AMOUNT.toFixed(2)}</span>
          </div>
        </div>

        <div className="sm-pay-what">
          <div className="sm-pay-what-item"><Icon name="check" size={16} /> Valid for today</div>
          <div className="sm-pay-what-item"><Icon name="check" size={16} /> No account required</div>
          <div className="sm-pay-what-item"><Icon name="check" size={16} /> Permit activates instantly after payment</div>
        </div>

        {step === 'form' && (
          <form onSubmit={handleContinue} className="sm-pay-form">
            <div className="form-group">
              <label className="sm-pay-label">License Plate *</label>
              <input
                className={`sm-pay-input ${errors.licensePlate ? 'input-error' : ''}`}
                placeholder="e.g. ABC1234"
                value={formData.licensePlate}
                onChange={e => setFormData(p => ({ ...p, licensePlate: e.target.value.toUpperCase() }))}
                autoCapitalize="characters"
                maxLength={10}
              />
              {errors.licensePlate && <div className="sm-pay-field-error">{errors.licensePlate}</div>}
            </div>
            <div className="form-group">
              <label className="sm-pay-label">State</label>
              <select
                className="sm-pay-select"
                value={formData.state}
                onChange={e => setFormData(p => ({ ...p, state: e.target.value }))}
              >
                {US_STATES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="sm-pay-label">Email <span className="optional">(optional, for receipt)</span></label>
              <input
                type="email"
                className={`sm-pay-input ${errors.email ? 'input-error' : ''}`}
                placeholder="your@email.com"
                value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
              />
              {errors.email && <div className="sm-pay-field-error">{errors.email}</div>}
            </div>
            <button type="submit" className="btn btn-primary btn-lg sm-pay-btn">
              Continue to Payment →
            </button>
          </form>
        )}

        {step === 'pay' && (
          <div>
            <div className="sm-pay-summary">
              <span>{formData.licensePlate} · {formData.state}</span>
              <button className="sm-pay-edit" onClick={() => setStep('form')}>Edit</button>
            </div>
            <StripeWrapper>
              <PaymentForm
                licensePlate={formData.licensePlate}
                state={formData.state}
                email={formData.email}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </StripeWrapper>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpaceMonkeyPayPage;
